import type { DrawerButtonsFooter } from "@components/contracts/Drawer.types";
import type { DrawerFilters } from "@pages/Dashboard/contexts/Filter.context";
import { useMemo } from "react";

interface UseFiltersDrawerButtonsProps {
  drawerFilters: DrawerFilters;
  onApply: () => void;
  onCancel: () => void;
  onClear: () => void;
}

const createButton = (
  key: string,
  label: string,
  buttonStyle: "primary" | "secondary" | "tertiary",
  onClick: () => void
) => ({
  key,
  type: "button" as const,
  buttonStyle,
  size: "md" as const,
  disabled: false,
  loading: false,
  blockedWidth: false,
  label,
  onClick,
});

export function useFiltersDrawerButtons({
  drawerFilters,
  onApply,
  onCancel,
  onClear,
}: UseFiltersDrawerButtonsProps) {
  const hasAppliedFilters = useMemo(
    () => drawerFilters.status.length > 0 || drawerFilters.channels.length > 0,
    [drawerFilters]
  );

  const buttonsFooter = useMemo<DrawerButtonsFooter>(() => {
    const cancelButton = createButton(
      "button-cancel",
      "Cancelar",
      "secondary",
      onCancel
    );

    const applyButton = createButton(
      "button-apply",
      "Aplicar",
      "primary",
      onApply
    );

    const clearButton = createButton(
      "button-clear",
      "Limpar",
      "tertiary",
      onClear
    );

    if (hasAppliedFilters) {
      return [applyButton, cancelButton, clearButton];
    }

    return [applyButton, cancelButton];
  }, [hasAppliedFilters, onApply, onCancel, onClear]);

  return buttonsFooter;
}
